import type { SupportBootOptions, SupportMessengerProvider } from '../types';
import { toSupportWidgetLocale } from './locale';

/** The only place this provider's name is spelled out — factory.ts imports this constant
 *  rather than hardcoding the string, so swapping providers never touches factory.ts. */
export const SUPPORT_PROVIDER_NAME = 'intercom';

type WidgetCommand = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    Intercom?: WidgetCommand;
  }
}

const WIDGET_SCRIPT_ID = 'support-messenger-widget-script';

/** How long openNewMessage waits for the widget's onShow before reporting it did not open. */
export const SHOW_TIMEOUT_MS = 1500;

/** Installs the vendor's own queueing shim (its official install snippet, ported to
 *  TypeScript): calls made before the real script has loaded are queued and replayed once
 *  it initializes itself, so boot() never has to await script-load. */
function installQueueShim(): WidgetCommand {
  const queue: unknown[][] = [];
  const command: WidgetCommand = (...args: unknown[]) => {
    queue.push(args);
  };
  command.q = queue;
  return command;
}

export class IntercomMessengerProvider implements SupportMessengerProvider {
  private loadPromise: Promise<void> | null = null;
  private visible = false;
  private shownWaiters: Array<() => void> = [];

  constructor(private readonly appId: string) {}

  load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise; // memoized: load() is safe to call repeatedly

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      this.loadPromise = Promise.reject(new Error('support widget: no DOM available'));
      return this.loadPromise;
    }

    if (typeof window.Intercom !== 'function') {
      window.Intercom = installQueueShim();
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(WIDGET_SCRIPT_ID);
      if (existing) {
        // A script tag already exists (e.g. a previous provider instance in this tab) — we
        // can't re-observe its past load/error events, so resolve optimistically rather than
        // hang forever.
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = WIDGET_SCRIPT_ID;
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${this.appId}`;
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => reject(new Error('support widget script failed to load')));
      document.head.appendChild(script);
    });
    return this.loadPromise;
  }

  async boot(opts: SupportBootOptions): Promise<void> {
    await this.load(); // throws (never resolves) if the script failed to load
    // The same attributes carried as JWT claims do NOT surface on the contact record
    // (empirically verified in a live test) — spreading them into the boot payload itself is
    // what actually populates/searches them. null/undefined are omitted (never sent as a
    // literal null attribute); identity (user_id/email/name) is deliberately NOT spread here —
    // it stays in the JWT only, so boot and token can never disagree about who the user is.
    const cleanAttributes = Object.fromEntries(
      Object.entries(opts.attributes ?? {}).filter(([, v]) => v !== null && v !== undefined),
    );
    window.Intercom?.('boot', {
      api_base: 'https://api-iam.intercom.io',
      app_id: this.appId,
      intercom_user_jwt: opts.userJwt,
      language_override: toSupportWidgetLocale(opts.languageOverride),
      ...cleanAttributes,
    });
    this.watchVisibility();
  }

  /** onShow / onHide are the only signal that a boot really took: the vendor reports a failed
   *  boot to nobody ("ready only fires on a successful boot … there is no timeout"). Registered
   *  after every boot — the docs do not say whether handlers survive shutdown. */
  private watchVisibility(): void {
    window.Intercom?.('onShow', () => {
      this.visible = true;
      const waiters = this.shownWaiters;
      this.shownWaiters = [];
      waiters.forEach((w) => w());
    });
    window.Intercom?.('onHide', () => {
      this.visible = false;
    });
  }

  async bootAnonymous(opts: { languageOverride: string }): Promise<void> {
    await this.load(); // throws (never resolves) if the script failed to load
    // No intercom_user_jwt, no custom attributes — this visitor has no Flat Hero account (or we
    // simply don't know it yet); confirmed empirically (2026-07-21) that an anonymous boot still
    // succeeds even with Messenger Security enforcement ON (enforcement only applies to
    // IDENTIFIED boots — there's no identity here to spoof). Opens the window immediately: the
    // click that triggered this call IS the request to see the messenger, not just launch it.
    window.Intercom?.('boot', {
      api_base: 'https://api-iam.intercom.io',
      app_id: this.appId,
      language_override: toSupportWidgetLocale(opts.languageOverride),
    });
    this.watchVisibility();
    window.Intercom?.('show');
  }

  update(opts: Partial<SupportBootOptions> = {}): void {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') return;
    if (opts.languageOverride) {
      window.Intercom('update', { language_override: toSupportWidgetLocale(opts.languageOverride) });
    } else {
      window.Intercom('update');
    }
  }

  shutdown(): void {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') return;
    window.Intercom('shutdown');
    this.visible = false;
  }

  openNewMessage(prefill?: string, timeoutMs: number = SHOW_TIMEOUT_MS): Promise<boolean> {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') return Promise.resolve(false);
    const command = window.Intercom;
    if (this.visible) {
      command('showNewMessage', prefill);
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (opened: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.shownWaiters = this.shownWaiters.filter((w) => w !== shown);
        resolve(opened);
      };
      const shown = () => finish(true);
      this.shownWaiters.push(shown);
      const timer = setTimeout(() => finish(false), timeoutMs);
      command('showNewMessage', prefill);
    });
  }

  conversationUrl(providerConversationId: string): string {
    // Convention 16: read before wiring. There is no PUBLIC, versioned API for this — the
    // vendor's developer docs (developers.intercom.com) don't publish an Inbox web-app URL
    // spec; this is their internal SPA route, only knowable by observing the browser address
    // bar while a teammate has a conversation open (corroborated across multiple independent
    // community threads describing exactly that observed shape, e.g.
    // community.intercom.com/api-webhooks-23/what-is-the-logic-to-create-a-conversation-url-...).
    // Repercussion to flag (per Convention 16): being an unversioned internal route, the vendor
    // could change this shape without notice — unlike everything else this provider does
    // (Messenger Security JWT, Canvas Kit, the conversation webhook), which are all documented,
    // versioned APIs. If this link ever 404s, the fallback is unchanged either way: operators
    // can still find the conversation inside the Inbox by its provider_conversation_id.
    return `https://app.intercom.com/a/inbox/${this.appId}/inbox/shared/all/conversation/${providerConversationId}`;
  }
}

// Exported as a plain factory function (not the class itself) so factory.ts never has to
// import — and therefore never has to spell out — this vendor's name in its own source.
export function createSupportMessengerProvider(appId: string): SupportMessengerProvider {
  return new IntercomMessengerProvider(appId);
}
